// src/components/Modal.js

import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import {
    FaClipboard,
    FaCheckCircle,
    FaQuestionCircle,
    FaExclamationCircle,
    FaLock,
    FaWrench,
    FaExclamationTriangle, FaInfoCircle, FaEyeSlash, FaEye
} from "react-icons/fa";
import {useTranslation} from "react-i18next";

const Modal = ({
                   show,
                   title,
                   message,
                   type = "success",
                   icon,
                   password,
                   copyableText, // ✅ toegevoegd
                   onConfirm,
                   onClose,
                   status,
                   children,
                   footer,
                   hideDefaultActions = false,
                   variant = "default",
                   dialogClassName = "",
                   showIcon = true
               }) => {
    const { t } = useTranslation();
    const isParentVariant = variant === "parent";
    const isLdsVariant = variant === "lds";

    const textToCopy = copyableText || password;
    const [showText, setShowText] = React.useState(false);
    const [copied, setCopied] = React.useState(false);
    const dialogRef = useRef(null);
    const onCloseRef = useRef(onClose);
    const titleId = React.useId();
    onCloseRef.current = onClose;

    useEffect(() => {
        if (!show) return undefined;
        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                onCloseRef.current();
            }
            if (event.key === "Tab" && dialogRef.current) {
                const focusable = Array.from(
                    dialogRef.current.querySelectorAll(
                        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
                    )
                );
                if (!focusable.length) return;
                const first = focusable[0];
                const last = focusable[focusable.length - 1];
                if (event.shiftKey && document.activeElement === first) {
                    event.preventDefault();
                    last.focus();
                } else if (!event.shiftKey && document.activeElement === last) {
                    event.preventDefault();
                    first.focus();
                }
            }
        };
        const previousFocus = document.activeElement;
        window.requestAnimationFrame(() => {
            const preferred = dialogRef.current?.querySelector("[autofocus]");
            (preferred || dialogRef.current)?.focus();
        });
        window.addEventListener("keydown", handleKeyDown);
        return () => {
            window.removeEventListener("keydown", handleKeyDown);
            previousFocus?.focus?.();
        };
    }, [show]);

    if (!show) return null;

    // ✅ Dynamically Choose Icon Based on Type
    const getIcon = () => {
        if (icon) return icon;
        const iconClassByType = isParentVariant
            ? {
                password: "text-teal-600 dark:text-teal-300 text-5xl",
                confirm: "text-amber-500 dark:text-amber-300 text-5xl",
                error: "text-rose-600 dark:text-rose-300 text-5xl",
                success: "text-emerald-600 dark:text-emerald-300 text-5xl",
                warning: "text-amber-500 dark:text-amber-300 text-5xl",
                info: "text-teal-600 dark:text-teal-300 text-5xl",
                manage: "text-emerald-600 dark:text-emerald-300 text-5xl",
            }
            : isLdsVariant
              ? {
                  password: "h-5 w-5 text-indigo-600 dark:text-indigo-300",
                  confirm: "h-5 w-5 text-amber-600 dark:text-amber-300",
                  error: "h-5 w-5 text-red-600 dark:text-red-300",
                  success: "h-5 w-5 text-emerald-600 dark:text-emerald-300",
                  warning: "h-5 w-5 text-amber-600 dark:text-amber-300",
                  info: "h-5 w-5 text-indigo-600 dark:text-indigo-300",
                  manage: "h-5 w-5 text-indigo-600 dark:text-indigo-300",
                }
              : {
                password: "text-blue-500 text-5xl",
                confirm: "text-yellow-500 text-5xl",
                error: "text-red-500 text-5xl",
                success: "text-green-500 text-5xl",
                warning: "text-yellow-500 text-5xl",
                info: "text-blue-500 text-5xl",
                manage: "text-indigo-500 text-5xl",
            };
        const iconClass = iconClassByType[type] || iconClassByType.success;
        if (type === "password") return <FaLock className={iconClass} />; // 🔒 Lock Icon for password changes
        if (type === "confirm") return <FaQuestionCircle className={iconClass} />;
        if (type === "error") return <FaExclamationCircle className={iconClass} />;
        if (type === "success") return <FaCheckCircle className={iconClass} />;
        if (type === "warning") return <FaExclamationTriangle className={iconClass} />;
        if (type === "info") return <FaInfoCircle className={iconClass} />;
        if (type === "manage") return <FaWrench className={iconClass} />;
        return <FaCheckCircle className={iconClass} />;
    };

    const overlayClassName = isParentVariant
        ? "fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/35 backdrop-blur-xl transition-opacity duration-300 ease-in-out dark:bg-slate-950/65"
        : isLdsVariant
          ? "fixed inset-0 z-[9999] flex items-end justify-center bg-slate-950/50 p-0 backdrop-blur-sm transition-opacity duration-200 ease-out sm:items-center sm:p-6 dark:bg-slate-950/70"
        : "fixed inset-0 z-[9999] flex items-center justify-center backdrop-blur-md bg-gray-900 bg-opacity-40 dark:bg-opacity-50 transition-opacity duration-300 ease-in-out";

    const dialogBaseClassName = isParentVariant
        ? "w-[min(92vw,28rem)] rounded-3xl border border-white/50 bg-gradient-to-br from-white/95 via-emerald-50/85 to-lime-50/70 p-7 text-center text-slate-800 shadow-[0_28px_80px_-30px_rgba(6,78,59,0.75)] backdrop-blur-2xl transform scale-95 transition-transform duration-300 ease-in-out scale-100 dark:border-emerald-900/50 dark:from-slate-900/95 dark:via-emerald-950/55 dark:to-slate-900/90 dark:text-slate-100"
        : isLdsVariant
          ? "max-h-[92vh] w-full overflow-y-auto rounded-t-[28px] border border-slate-200 bg-white p-5 text-left text-slate-900 shadow-[0_28px_80px_rgba(15,23,42,0.24)] transition duration-200 ease-out dark:border-slate-700 dark:bg-slate-900 dark:text-white sm:rounded-[28px] sm:p-6"
        : "bg-white dark:bg-gray-800 dark:text-white p-8 rounded-lg shadow-lg w-96 max-w-sm text-center transform scale-95 transition-transform duration-300 ease-in-out scale-100";

    const statusClassName = isParentVariant
        ? "mb-1 text-sm font-semibold text-slate-500 dark:text-slate-400"
        : "text-sm text-gray-500 dark:text-gray-400 mb-1";

    const titleClassName = isParentVariant
        ? "mb-3 text-2xl font-bold text-slate-900 dark:text-white"
        : isLdsVariant
          ? "mb-3 text-xl font-bold tracking-tight text-slate-950 dark:text-white sm:text-2xl"
        : "text-2xl font-bold mb-4";

    const messageClassName = isParentVariant
        ? "mb-6 text-sm font-medium leading-6 text-slate-600 dark:text-slate-300"
        : isLdsVariant
          ? "mb-5 text-sm leading-6 text-slate-600 dark:text-slate-300"
        : "mb-6 text-gray-700 dark:text-gray-300";

    const closeButtonClassName = isParentVariant
        ? "w-full rounded-full border border-emerald-500/40 bg-gradient-to-r from-emerald-600 to-teal-700 px-6 py-3 text-base font-semibold text-white shadow-[0_16px_32px_-18px_rgba(5,150,105,0.8)] transition hover:from-emerald-500 hover:to-teal-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/70 dark:border-emerald-400/40 dark:from-emerald-700 dark:to-teal-800"
        : "bg-indigo-600 text-white px-6 py-3 rounded-lg text-lg hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 w-full";

    const modalMarkup = (
        <div
            className={overlayClassName}
            onClick={onClose} // ✅ Clicking outside closes modal
        >
            <div
                ref={dialogRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                tabIndex={-1}
                className={`${dialogBaseClassName} ${dialogClassName}`}
                onClick={(e) => e.stopPropagation()} // ✅ Prevent closing on inner clicks
            >
                {/* ✅ Dynamic Icon */}
                {showIcon && (
                    <div
                        className={
                            isLdsVariant
                                ? "mb-4 flex h-11 w-11 items-center justify-center rounded-2xl bg-indigo-50 ring-1 ring-indigo-100 dark:bg-indigo-400/10 dark:ring-indigo-400/20"
                                : "flex justify-center mb-4"
                        }
                    >
                        {getIcon()}
                    </div>
                )}

                {/*statuscode if available*/}
                {status && (
                    <div className={statusClassName}>
                        {status}
                    </div>
                )}

                {/* ✅ Modal Title */}
                <h2 id={titleId} className={titleClassName}>{title}</h2>

                {/* ✅ Custom Content (e.g., Password Change Form) */}
                {children ? (
                    children
                ) : textToCopy ? (
                    <div className="mb-6 text-left">
                        <p className="text-gray-600 dark:text-gray-300 font-semibold mb-2">
                            {t("common.modal.copy_label") || "Temporary password:"}
                        </p>
                        <div className="flex items-center bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md px-4 py-2 font-mono text-base">
            <span className="flex-1 truncate">
                {showText ? textToCopy : "•".repeat(textToCopy.length)}
            </span>
                            <button
                                onClick={() => setShowText(!showText)}
                                className="ml-2 text-gray-500 hover:text-gray-700"
                                title={showText ? t("common.actions.hide") || "Hide" : t("common.actions.show") || "Show"}
                            >
                                {showText ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
                            </button>
                            <button
                                onClick={() => {
                                    navigator.clipboard.writeText(textToCopy);
                                    setCopied(true);
                                    setTimeout(() => setCopied(false), 1500);
                                }}
                                className="ml-2 text-gray-500 hover:text-gray-700"
                                title={t("common.actions.copy") || "Copy to clipboard"}
                            >
                                <FaClipboard size={18} />
                            </button>
                        </div>
                        {copied && (
                            <p className="text-green-600 dark:text-green-400 text-sm mt-1 animate-pulse">
                                {t("common.actions.copied") || "Copied!"}
                            </p>
                        )}
                    </div>
                ) : message ? (
                    <p className={messageClassName}>
                        {typeof message === "function" ? message() : message}
                    </p>
                ) : null}

                {/* ✅ Different Button Options */}
                {footer ? (
                    footer
                ) : type === "confirm" ? (
                    <div className="flex justify-between">
                        <button
                            onClick={onClose}
                            className="bg-gray-300 dark:bg-gray-700 text-black dark:text-white px-4 py-2 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-600 w-1/2 mr-2"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={onConfirm}
                            className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600 w-1/2"
                        >
                            Confirm
                        </button>
                    </div>
                ) : hideDefaultActions ? null : (
                   <button
                     onClick={onClose}
                     className={closeButtonClassName}
                   >
                     Close
                   </button>
               )}
            </div>
        </div>
    );

    if (typeof document === "undefined" || !document.body) {
        return modalMarkup;
    }

    return createPortal(modalMarkup, document.body);
};

export default Modal;
